import { FC } from 'react';
import { THEME_CLASSES } from '../../constants/themeConstants';
import useCSVExport from '../../hooks/useCSVExport';

interface Column {
  field: string;
  headerName: string;
}

interface ExportButtonProps {
  data: any[];
  columns: Column[];
  filename: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Reusable CSV Export Button Component
 * Exports data directly from props using client-side CSV generation
 */
const ExportButton: FC<ExportButtonProps> = ({
  data,
  columns,
  filename,
  disabled = false,
  className = ''
}) => {
  const { exportCSV, isExporting, error } = useCSVExport();

  const handleExport = async () => {
    if (!data || data.length === 0) {
      return;
    }

    // Extract headers and field names from columns
    const headers = columns.map(col => col.headerName);
    const fields = columns.map(col => col.field);

    // Map data to use field names as keys
    const exportData = data.map(row => {
      const mappedRow: any = {};
      fields.forEach((field, index) => {
        mappedRow[headers[index]] = row[field];
      });
      return mappedRow;
    });

    await exportCSV(exportData, {
      filename,
      headers
    });
  };

  const isDisabled = disabled || isExporting || !data || data.length === 0;

  return (
    <div className="relative">
      <button
        onClick={handleExport}
        disabled={isDisabled}
        className={`
          inline-flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-md
          transition-colors duration-200
          ${isDisabled
            ? `${THEME_CLASSES.text.secondary} bg-gray-100 dark:bg-gray-800 cursor-not-allowed`
            : `${THEME_CLASSES.button.secondary} ${THEME_CLASSES.hover.background}`
          }
          ${className}
        `}
        title={isDisabled ? 'No data to export' : 'Export current view to CSV'}
      >
        {isExporting ? (
          <>
            {/* Loading spinner */}
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Exporting...</span>
          </>
        ) : (
          <>
            {/* Download icon */}
            <svg
              className="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span>Export CSV</span>
          </>
        )}
      </button>

      {/* Error message */}
      {error && (
        <div className="absolute top-full left-0 mt-1 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700 whitespace-nowrap z-10">
          {error}
        </div>
      )}
    </div>
  );
};

export default ExportButton;